import React, { useState, useMemo, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { PackageSearch, ShieldCheck, Sparkles, FlaskConical } from 'lucide-react';
import useSeo from '../hooks/useSeo';
import { SiteHeader, SiteFooter } from './ServicesIndex';
import { PRODUCTS, PRODUCT_CATEGORIES } from '../data/productsData';
import { ProductCard, ProductCardSkeleton } from '../components/products/ProductCard';
import { ProductModal } from '../components/products/ProductModal';
import { ProductSearch } from '../components/products/ProductSearch';
import './ProductsPage.css';

const SKELETON_COUNT = 8;

export default function ProductsPage({ onOpenCalculator }) {
  const [query, setQuery] = useState('');
  const [category, setCategory] = useState('all');
  const [activeProduct, setActiveProduct] = useState(null);
  const [loading, setLoading] = useState(true);

  useSeo(
    'Professional Pest Control Products',
    'Explore professional pest control products including termite, mosquito, cockroach, rodent, ant, bed bug, and general pest management solutions from EntoRid.'
  );

  // Brief skeleton on initial load and on category change for smooth perceived loading
  useEffect(() => {
    setLoading(true);
    const t = setTimeout(() => setLoading(false), 400);
    return () => clearTimeout(t);
  }, [category]);

  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase();
    return PRODUCTS.filter((p) => {
      if (category !== 'all' && p.category !== category) return false;
      if (!q) return true;
      return (
        p.name.toLowerCase().includes(q) ||
        p.shortDescription.toLowerCase().includes(q) ||
        p.fullDescription.toLowerCase().includes(q) ||
        p.category.toLowerCase().includes(q)
      );
    });
  }, [query, category]);

  return (
    <div className="min-h-screen bg-slate-50" style={{ fontFamily: 'Poppins, Inter, sans-serif' }}>
      <SiteHeader onOpenCalculator={onOpenCalculator} />

      {/* Breadcrumb */}
      <div className="bg-white border-b border-slate-100">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-3 text-xs sm:text-sm text-gray-500 overflow-x-auto whitespace-nowrap">
          <Link to="/" className="hover:text-green-700">Home</Link>
          <span className="mx-2">›</span>
          <span className="text-gray-700 font-medium">Products</span>
        </div>
      </div>

      {/* ============ HERO ============ */}
      <section className="relative overflow-hidden bg-gradient-to-br from-slate-900 via-slate-900 to-emerald-900 text-white">
        <div aria-hidden className="pp-hero-blob absolute -top-24 -right-16 w-80 h-80 rounded-full bg-emerald-500/20 blur-3xl" />
        <div aria-hidden className="pp-hero-blob pp-hero-blob-2 absolute -bottom-28 -left-20 w-80 h-80 rounded-full bg-teal-400/15 blur-3xl" />
        <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12 sm:py-16 lg:py-20 text-center">
          <span className="pp-hero-anim inline-flex items-center gap-2 rounded-full bg-white/10 backdrop-blur-sm border border-white/15 px-3.5 py-1.5 text-[11px] sm:text-xs font-semibold">
            <FlaskConical size={13} className="text-emerald-300" /> Industry-Grade Formulations
          </span>
          <h1 className="pp-hero-anim pp-hero-anim-delay-1 mt-4 text-3xl sm:text-4xl lg:text-5xl font-bold tracking-tight" data-testid="products-hero-title">
            Professional Pest Control Products
          </h1>
          <p className="pp-hero-anim pp-hero-anim-delay-2 mt-4 text-sm sm:text-lg text-white/80 max-w-2xl mx-auto leading-relaxed">
            Industry-grade pest management solutions trusted for homes, businesses,
            offices, hotels, warehouses, and commercial properties.
          </p>

          {/* Coming Soon banner — clearly communicates we are not selling yet */}
          <div className="pp-hero-anim pp-hero-anim-delay-2 mt-6 mx-auto max-w-2xl bg-amber-400/15 border border-amber-300/40 rounded-2xl px-4 py-3 backdrop-blur-sm" data-testid="products-coming-soon-banner">
            <div className="flex items-start gap-3 text-left">
              <div className="flex-shrink-0 w-9 h-9 rounded-full bg-amber-400 text-amber-950 flex items-center justify-center text-lg font-bold shadow-md">🚀</div>
              <div>
                <div className="text-amber-200 font-bold text-sm sm:text-base">Products Launching Soon</div>
                <p className="text-xs sm:text-sm text-amber-100/85 leading-relaxed mt-0.5">
                  We currently offer professional pest control <span className="font-semibold text-white">services</span>. Our retail product line is launching shortly — tap any product to get notified on WhatsApp.
                </p>
              </div>
            </div>
          </div>

          <div className="pp-hero-anim pp-hero-anim-delay-2 mt-6 flex flex-wrap items-center justify-center gap-2 sm:gap-3 text-[11px] sm:text-xs">
            {[
              { Icon: ShieldCheck, label: 'Govt-Approved Chemicals' },
              { Icon: Sparkles, label: 'Eco-Friendly & Odourless' },
              { Icon: PackageSearch, label: `${PRODUCTS.length}+ Professional Products` },
            ].map(({ Icon, label }) => (
              <span key={label} className="inline-flex items-center gap-1.5 rounded-full bg-white/8 border border-white/10 px-3 py-1.5 font-medium text-white/85">
                <Icon size={13} className="text-emerald-300" /> {label}
              </span>
            ))}
          </div>
        </div>
      </section>

      {/* ============ SEARCH ============ */}
      <section className="relative z-30 -mt-7 sm:-mt-8 px-4 sm:px-6 lg:px-8">
        <div className="max-w-7xl mx-auto">
          <ProductSearch query={query} onQueryChange={setQuery} />
        </div>
      </section>

      {/* ============ STICKY CATEGORY FILTERS ============ */}
      <div className="sticky top-0 z-20 mt-6 bg-slate-50/85 backdrop-blur-md border-b border-slate-200/70 supports-[backdrop-filter]:bg-slate-50/70">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="pp-filter-scroll flex gap-2 py-3 overflow-x-auto" data-testid="products-category-filters">
            {PRODUCT_CATEGORIES.map((c) => {
              const active = category === c.id;
              return (
                <button
                  key={c.id}
                  type="button"
                  onClick={() => setCategory(c.id)}
                  className={`flex-shrink-0 px-4 h-9 rounded-full text-xs sm:text-sm font-semibold whitespace-nowrap transition-[background-color,color,box-shadow] duration-250 ${
                    active
                      ? 'bg-emerald-600 text-white shadow-[0_4px_14px_rgba(5,150,105,0.35)]'
                      : 'bg-white text-slate-600 border border-slate-200 hover:border-emerald-300 hover:text-emerald-700'
                  }`}
                  data-testid={`category-filter-${c.id}`}
                >
                  {c.label}
                </button>
              );
            })}
          </div>
        </div>
      </div>

      {/* ============ RESULTS ============ */}
      <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8 sm:py-10">
        {/* Result count */}
        <div key={`${filtered.length}-${category}-${query}`} className="pp-result-count mb-6 flex items-center justify-between">
          <p className="text-sm sm:text-base font-semibold text-slate-700" data-testid="products-result-count">
            {filtered.length > 0
              ? `${filtered.length} Product${filtered.length === 1 ? '' : 's'} Found`
              : 'No Products Found'}
          </p>
        </div>

        {loading ? (
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-5 sm:gap-6" data-testid="products-skeleton-grid">
            {Array.from({ length: SKELETON_COUNT }).map((_, i) => <ProductCardSkeleton key={i} />)}
          </div>
        ) : filtered.length > 0 ? (
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-5 sm:gap-6" data-testid="products-grid">
            {filtered.map((p, i) => (
              <ProductCard key={p.id} product={p} query={query} index={i} onLearnMore={setActiveProduct} />
            ))}
          </div>
        ) : (
          <div className="pp-result-count text-center py-16 sm:py-24" data-testid="products-empty-state">
            <div className="mx-auto w-16 h-16 rounded-2xl bg-slate-100 flex items-center justify-center">
              <PackageSearch size={28} className="text-slate-400" />
            </div>
            <h3 className="mt-5 text-lg font-bold text-slate-800">No Products Found</h3>
            <p className="mt-2 text-sm text-slate-500 max-w-sm mx-auto">
              We couldn't find any products matching your search. Try a different keyword or browse all products.
            </p>
            <button
              type="button"
              onClick={() => { setQuery(''); setCategory('all'); }}
              className="mt-5 inline-flex items-center px-5 h-10 rounded-full bg-emerald-600 text-white text-sm font-semibold hover:bg-emerald-700 transition-colors"
              data-testid="products-reset-filters"
            >
              View All Products
            </button>
          </div>
        )}
      </main>

      <SiteFooter />

      {/* Product details modal */}
      <ProductModal product={activeProduct} onClose={() => setActiveProduct(null)} />
    </div>
  );
}
